import { useCallback, useMemo } from 'react';

import { useAuthInfo } from '@propelauth/react';

export enum FetchMethod {
  GET = 'GET',
  PATCH = 'PATCH',
  POST = 'POST',
  DELETE = 'DELETE',
  PUT = 'PUT',
}

interface UseGetFetchConfig {
  method: FetchMethod;
  data?: Record<string, any>;
  isFormData?: boolean;
}

export interface FetchConfig {
  method: FetchMethod;
  mode: RequestMode;
  cache: RequestCache;
  credentials: RequestCredentials;
  headers: Headers;
}

export const useFetcher = () => {
  const authInfo = useAuthInfo();

  const fetcher = async <T = unknown>(url: string): Promise<T> => {
    const accessToken = await authInfo.tokens.getAccessToken();

    const res = await fetch(url, {
      method: FetchMethod.GET,
      mode: 'cors' as RequestMode,
      cache: 'no-cache' as RequestCache,
      credentials: 'same-origin' as RequestCredentials,
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      }),
    });

    if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);

    const json = await res.json();
    return json;
  };

  return fetcher;
};

const useGetFetchConfig = () => {
  // TODO: Deprecate
  const authInfo = useAuthInfo();
  const accessToken = authInfo.accessToken;

  const fetchConfigGET: FetchConfig = useMemo(
    () => ({
      method: FetchMethod.GET,
      mode: 'cors' as RequestMode,
      cache: 'no-cache' as RequestCache,
      credentials: 'same-origin' as RequestCredentials,
      headers: new Headers({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      }),
    }),
    [accessToken],
  );

  const getFetchConfig = useCallback(
    ({ method, data, isFormData }: UseGetFetchConfig) => {
      const body = isFormData ? (data as FormData) : JSON.stringify(data);

      const headers: HeadersInit = isFormData
        ? {
            Authorization: `Bearer ${accessToken}`,
          }
        : {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          };

      return {
        method,
        cache: 'no-cache' as RequestCache,
        credentials: 'same-origin' as RequestCredentials,
        headers: new Headers(headers),
        body: data ? body : undefined,
      };
    },
    [accessToken],
  );

  return {
    fetchConfigGET,
    getFetchConfig,
  };
};

export default useGetFetchConfig;
